import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { renderAiOutput } from '@/components/agents/AiOutput';
import { AppShell } from '@/components/layout/AppShell';
import { useToast, useConfirm } from '@/components/ui';
import { aiResultsApi, AiResult } from '@/lib/aiResultsApi';

const KIND_LABELS: Record<string, string> = {
  prospect: 'Prospect',
  keigo_compose: 'Keigo',
  reply_coach: 'Reply coach',
  deal_coach: 'Deal coach',
  signal_summary: 'Signal summary',
  sales_qa: 'Sales Q&A',
};

function formatCost(cents: number, freeLabel: string): string {
  if (!cents) return freeLabel;
  return `$${(cents / 100).toFixed(2)}`;
}

export function AiResultsPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [kind, setKind] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['ai-results', wsId, kind],
    queryFn: () => aiResultsApi.list({ workspaceId: wsId, agentKind: kind || undefined, limit: 200 }),
    enabled: !!wsId,
  });
  const items = list.data?.items ?? [];

  const pin = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => aiResultsApi.pin(id, pinned),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-results', wsId] }),
    onError: () => toast.error(t('common.save_failed')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => aiResultsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-results', wsId] });
      toast.success(t('ai_results.deleted'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const copy = async (r: AiResult) => {
    await navigator.clipboard.writeText(r.output);
    toast.success(t('ai_results.copied'));
  };

  const askDelete = async (r: AiResult) => {
    const ok = await confirm({
      title: t('ai_results.confirm_delete_title'),
      description: t('ai_results.confirm_delete_body'),
      confirmLabel: t('ai_results.delete'),
      variant: 'danger',
    });
    if (ok) remove.mutate(r.id);
  };

  const kinds = Array.from(new Set(items.map((r) => r.agentKind)));

  return (
    <AppShell titleKey="ai_results.title">
      <p className="text-sm text-ink-500 mb-4">{t('ai_results.subtitle')}</p>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="rounded-md border border-ink-300 px-3 py-1.5 text-sm"
        >
          <option value="">{t('ai_results.filter_all')}</option>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {KIND_LABELS[k] ?? k}
            </option>
          ))}
        </select>
        <span className="text-xs text-ink-500">{items.length}</span>
      </div>

      {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}
      {!list.isLoading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500">
          <p className="text-sm">{t('ai_results.empty_title')}</p>
          <p className="text-xs mt-2">{t('ai_results.empty_sub')}</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((r) => {
          const open = openId === r.id;
          return (
            <div key={r.id} className="rounded-2xl border border-ink-200 bg-white">
              <div className="flex items-center gap-3 px-4 py-3">
                <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-ink-100 text-ink-600">
                  {KIND_LABELS[r.agentKind] ?? r.agentKind}
                </span>
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : r.id)}
                  className="flex-1 text-left text-sm font-medium text-ink-900 hover:text-accent-600 truncate"
                >
                  {r.pinned && <span className="text-accent-500 mr-1">★</span>}
                  {r.title}
                </button>
                <span className="hidden sm:inline text-xs text-ink-400">{r.model}</span>
                <span className="hidden sm:inline text-xs text-ink-400">
                  {formatCost(r.costCents, t('ai_results.free'))}
                </span>
                <span className="text-xs text-ink-400">
                  {new Date(r.createdAt).toLocaleDateString(i18n.language)}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => pin.mutate({ id: r.id, pinned: !r.pinned })}
                    className="text-xs px-2 py-1 rounded-md border border-ink-200 text-ink-600 hover:bg-ink-50"
                  >
                    {r.pinned ? t('ai_results.unpin') : t('ai_results.pin')}
                  </button>
                  <button
                    type="button"
                    onClick={() => copy(r)}
                    className="text-xs px-2 py-1 rounded-md border border-ink-200 text-ink-600 hover:bg-ink-50"
                  >
                    {t('ai_results.copy')}
                  </button>
                  <button
                    type="button"
                    onClick={() => askDelete(r)}
                    className="text-xs px-2 py-1 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50"
                  >
                    {t('ai_results.delete')}
                  </button>
                </div>
              </div>
              {open && (
                <div className="border-t border-ink-100 px-4 py-3 text-sm text-ink-800">
                  {renderAiOutput(r.output)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
